import Permission from "permissions/permission";
import { GeneralDocumentAccess } from "permissions/resources/documents/documents-permissions-resources";
import { LegalEntityAccess } from "permissions/resources/legal-entities/legal-entities-permissions-resources";
import { LegalEntityAccessType } from "permissions/resources/legal-entities/legal-entity-access-types";
import { KnowledgeBaseAccess } from "permissions/resources/knowledge-base/knowledge-base-permissions-resources";
import {
  MarketplaceAccess,
  MarketplaceDirectoryAccess,
  MarketplaceSearchAccess,
} from "permissions/resources/marketplace/marketplace-permissions-resources";
import { CommunicationsAccess } from "permissions/resources/messaging/messaging-permissions-resources";
import { NotificationsAccess } from "permissions/resources/notifications/notifications-permissions-resources";
import { AccessLevel, Scope } from "permissions/resources/permission-resource-types";
import { UserNetworkingAccess } from "permissions/resources/users/users-permissions-resources";
import {
  DiscountsAccess,
  FeeSchedulesAccess,
  ProposalsAccess,
  TeamsAccess,
  WorkAgreementsAccess,
} from "permissions/resources/work/work-permissions-resources";

export class SessionPermissions {
  public get permissions(): Permission[] {
    return [...this._permissions];
  }

  public get includesAccessToCommunications(): boolean {
    const permission = this._permissions.find((p) => p.resource instanceof CommunicationsAccess);
    if (!permission) return false;
    const resource = permission.resource as CommunicationsAccess;
    return (
      resource.accessLevel !== undefined &&
      resource.accessLevel !== null &&
      resource.accessLevel >= AccessLevel["Read-Only"]
    );
  }

  /**
   * Whether this session may read the entity-wide notifications feed.
   *
   * Read/write access to one's own notifications is inherent and never appears as a stored grant,
   * so it is not what this asks about (issue #4438). This mirrors the API's gate on
   * `GET /notifications/entity`, which asserts entity-wide scope at read-only, so both read and
   * read/write holders qualify.
   */
  public get includesEntityWideAccessToNotifications(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof NotificationsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as NotificationsAccess;
      return (
        !permission.isDisabled &&
        resource.scope !== undefined &&
        resource.scope !== null &&
        resource.scope >= Scope["Entity-Wide"]
      );
    });
  }

  // Mirrors the API's upload gate: Document.CreateAsync asserts GeneralDocumentAccess at
  // Need-to-Know scope and Read/Write access, so any scope qualifies and only the access
  // level is decisive. Kept in step with that assertion so the UI never offers an upload
  // the server will reject. A withdrawn grant answers nothing, as the server's assertion reads it.
  public get includesReadWriteAccessToGeneralDocuments(): boolean {
    const permissions = this._permissions.filter((p) => !p.isDisabled && p.resource instanceof GeneralDocumentAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as GeneralDocumentAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read/Write"]
      );
    });
  }

  public get includesSigningAuthority(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof LegalEntityAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as LegalEntityAccess;
      return resource.accessType === LegalEntityAccessType["Signing Authority"];
    });
  }

  public get includesAccessToAdministration(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof LegalEntityAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as LegalEntityAccess;
      return (
        (resource.accessType === LegalEntityAccessType["Member Information"] &&
          resource.scope >= Scope["Entity-Wide"] &&
          resource.accessLevel >= AccessLevel["Read/Write"]) ||
        (resource.accessType === LegalEntityAccessType["Member Permissions"] &&
          resource.scope >= Scope["Entity-Wide"]) ||
        (resource.accessType === LegalEntityAccessType["Member Invitations"] && resource.scope >= Scope["Entity-Wide"])
      );
    });
  }

  /**
   * Whether this session may read the organization's roster.
   *
   * Narrower than {@link includesAccessToAdministration}, and deliberately so: that one is true
   * for Member Permissions or Member Invitations as well, and the roster endpoint accepts
   * neither. `GET legal-entities/{id}/members` asserts Member Information at Entity-Wide and
   * Read-Only (`LegalEntityQueryService.FindEntityMemberPageAsync`), so anything that calls it has
   * to ask this question rather than the umbrella one.
   */
  public get includesReadAccessToMemberInformation(): boolean {
    return this.hasLegalEntityAccess(LegalEntityAccessType["Member Information"]);
  }

  /**
   * Whether this session may read the organization's pending invitations.
   *
   * `GET legal-entities/{id}/user-entity-member-invitations` asserts Member Invitations at
   * Entity-Wide and Read-Only. A page that asked on the strength of
   * {@link includesAccessToAdministration} spent a 403 and a console error on a reader who was
   * entitled to be there — they simply administer something else.
   */
  public get includesReadAccessToMemberInvitations(): boolean {
    return this.hasLegalEntityAccess(LegalEntityAccessType["Member Invitations"]);
  }

  /**
   * Whether this session may send the organization's invitations.
   *
   * `POST legal-entities/{id}/user-entity-member-invitations` and its `/bulk` roster both assert
   * Member Invitations at Entity-Wide and Read/Write, so a reader of the pending list is not
   * thereby an inviter. A withdrawn grant answers nothing, as with every other write here.
   */
  public get includesWriteAccessToMemberInvitations(): boolean {
    return this._permissions.some((permission) => {
      if (permission.isDisabled || !(permission.resource instanceof LegalEntityAccess)) return false;
      const resource = permission.resource;
      return (
        resource.accessType === LegalEntityAccessType["Member Invitations"] &&
        resource.scope >= Scope["Entity-Wide"] &&
        resource.accessLevel >= AccessLevel["Read/Write"]
      );
    });
  }

  /**
   * Whether this session may read its colleagues' permissions.
   *
   * `GET /permissions` asks for Member Permissions at Entity-Wide and Read-Only for any grantee but
   * the caller (`PermissionManagementService.GetPermissionsAsync`). Narrower than
   * {@link includesAccessToAdministration}, which Member Information or Member Invitations also
   * satisfy, and a person page opened on either of those would be refused every read it makes.
   */
  public get includesReadAccessToMemberPermissions(): boolean {
    return this.hasLegalEntityAccess(LegalEntityAccessType["Member Permissions"]);
  }

  /**
   * Whether this session may grant its colleagues permissions, signing authority included.
   *
   * `PermissionManagementService.AddPermissionAsync` asserts Member Permissions at Entity-Wide and
   * Read/Write, and so does handing a team over to somebody without signing authority
   * (`WorkAgreement.AssertMayGrantSigningAuthorityAsync`, #5836). A withdrawn grant answers nothing.
   */
  public get includesWriteAccessToMemberPermissions(): boolean {
    return this._permissions.some((permission) => {
      if (permission.isDisabled || !(permission.resource instanceof LegalEntityAccess)) return false;
      const resource = permission.resource;
      return (
        resource.accessType === LegalEntityAccessType["Member Permissions"] &&
        resource.scope >= Scope["Entity-Wide"] &&
        resource.accessLevel >= AccessLevel["Read/Write"]
      );
    });
  }

  public get includesAccessToWork(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof WorkAgreementsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as WorkAgreementsAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  public get includesAccessToProposals(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof ProposalsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as ProposalsAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  /**
   * Whether this session may DRAFT a proposal.
   *
   * `ProposalDraftingAdmission` asserts ProposalsAccess at Need-to-Know and Read/Write, so any
   * scope qualifies and the access level decides it. A reader of proposals is not a drafter.
   * Sending the draft is a separate, higher bar: see
   * {@link includesEntityWideReadWriteAccessToProposals} (#5795).
   */
  public get includesReadWriteAccessToProposals(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof ProposalsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as ProposalsAccess;
      return (
        !permission.isDisabled &&
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read/Write"]
      );
    });
  }

  /**
   * Whether this session may SEND a new proposal, which is a strictly higher bar than drafting one.
   *
   * The first submission of a proposal asserts ProposalsAccess at **Entity-Wide** scope and
   * Read/Write (`ProposalCollaborationCommandService.AssertMaySendOpeningOfferAsync`). Drafting
   * asserts only Need-to-Know. An opening offer commits the firm, so sending it is an entity-level
   * act. Drafting one is not (#5795). Counters on a proposal already sent, and revisions of a hired
   * agreement, are not gated here.
   *
   * {@link includesAccessToProposals} cannot stand in for this: it ignores scope entirely, so a member
   * holding Read/Write at Need-to-Know passes it and would only learn at Submit that the server was
   * never going to send their offer.
   */
  public get includesEntityWideReadWriteAccessToProposals(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof ProposalsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as ProposalsAccess;
      return (
        !permission.isDisabled &&
        resource.scope !== undefined &&
        resource.scope !== null &&
        resource.scope >= Scope["Entity-Wide"] &&
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read/Write"]
      );
    });
  }

  /**
   * Whether this session may read its own organization's firm roll-outs.
   *
   * `VendorRolloutBoardService.AssertMayReadAsync` asserts ProposalsAccess at **Entity-Wide** scope
   * and Read-Only when the caller asks about their own organization, so both read and read/write
   * holders qualify — unlike {@link includesEntityWideReadWriteAccessToProposals}, which is the
   * originating bar.
   *
   * Scope is what is decisive here and {@link includesAccessToProposals} ignores it: a member
   * holding Need-to-Know sees only the proposals they are on, whereas the board is every firm the
   * organization has invited. That is a supplier list, which is an entity-level thing to know.
   */
  public get includesEntityWideAccessToProposals(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof ProposalsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as ProposalsAccess;
      return (
        !permission.isDisabled &&
        resource.scope !== undefined &&
        resource.scope !== null &&
        resource.scope >= Scope["Entity-Wide"] &&
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  /**
   * Whether this session may set a discount that applies across the organization.
   *
   * `ClientProposalTemplatesController.UpdateClientProposalTemplate` asserts DiscountsAccess at
   * **Entity-Wide** scope and Read/Write, separately from the proposals assertion beside it. A
   * discount set on the proposal template configuration is applied to every firm the organization
   * onboards, so somebody who may build proposals but not price them does not acquire that by
   * coming through this door.
   */
  public get includesEntityWideReadWriteAccessToDiscounts(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof DiscountsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as DiscountsAccess;
      return (
        !permission.isDisabled &&
        resource.scope !== undefined &&
        resource.scope !== null &&
        resource.scope >= Scope["Entity-Wide"] &&
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read/Write"]
      );
    });
  }

  public get includesAccessToTeams(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof TeamsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as TeamsAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  /**
   * Whether this session may CREATE a team template, which is a higher bar than reading one.
   *
   * `TeamTemplateAdministrationService.AssertCanCreateTeamTemplateAsync` asserts TeamsAccess at
   * Need-to-Know and Read/Write, so the scope is not decisive and only the access level is. A
   * template is its creator's own record: authoring one grants them that template and nothing
   * else, so it never needed the entity-wide scope that reads the firm's others (#5771).
   *
   * {@link includesAccessToTeams} cannot stand in for this: it passes on Read-Only, and it does not
   * skip a disabled grant, so a member who may only read templates would be offered Create
   * Template and refused at Save (#4948).
   */
  public get includesReadWriteAccessToTeams(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof TeamsAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as TeamsAccess;
      return (
        !permission.isDisabled &&
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read/Write"]
      );
    });
  }

  public get includesAccessToFeeSchedules(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof FeeSchedulesAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as FeeSchedulesAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  /**
   * Whether this session may AUTHOR a fee schedule template, which is a higher bar than listing one.
   *
   * `FeeScheduleTemplate.CreateAsync` asserts FeeSchedulesAccess at Need-to-Know and Read/Write, so
   * unlike proposals and team templates the scope is not decisive here and only the access level is.
   * {@link includesAccessToFeeSchedules} cannot stand in for it: that one passes on Read-Only, so a
   * member who may only read the schedules was offered Create Template and refused at save (#4651).
   */
  public get includesReadWriteAccessToFeeSchedules(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof FeeSchedulesAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as FeeSchedulesAccess;
      return (
        !permission.isDisabled &&
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read/Write"]
      );
    });
  }

  public get includesAccessToKnowledgeBase(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof KnowledgeBaseAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as KnowledgeBaseAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  /**
   * Whether this user may write knowledge-base content for their own organization.
   *
   * Scope as well as access level, because the aggregates ask for both: a grant at need-to-know
   * read/write is not enough, and reading the access level alone reported its holder as an author. The
   * browser then offered New article and Edit to people whose every save the server refused.
   */
  public get includesWriteAccessToKnowledgeBase(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof KnowledgeBaseAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as KnowledgeBaseAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read/Write"] &&
        resource.scope !== undefined &&
        resource.scope !== null &&
        resource.scope >= Scope["Entity-Wide"]
      );
    });
  }

  public get includesReadAccessToMarketplace(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof MarketplaceAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as MarketplaceAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  public get includesReadWriteAccessToMarketplace(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof MarketplaceAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as MarketplaceAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read/Write"]
      );
    });
  }

  public get includesAccessToMarketplaceSearch(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof MarketplaceSearchAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as MarketplaceSearchAccess;
      return (
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  public get includesReadAccessToMarketplaceDirectory(): boolean {
    return this._permissions.some((permission) => {
      const resource = permission.resource;
      return !permission.isDisabled && resource instanceof MarketplaceDirectoryAccess &&
        resource.accessLevel !== undefined && resource.accessLevel >= AccessLevel["Read-Only"] &&
        resource.scope !== undefined && resource.scope >= Scope["Need-to-Know"];
    });
  }

  public get includesAccessToNetwork(): boolean {
    const permissions = this._permissions.filter((p) => p.resource instanceof UserNetworkingAccess);
    return permissions.some((permission) => {
      const resource = permission.resource as UserNetworkingAccess;
      return (
        !permission.isDisabled &&
        resource.accessLevel !== undefined &&
        resource.accessLevel !== null &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }

  constructor(private readonly _permissions: Permission[]) {}

  /** Entity-wide read of one legal-entity resource, which is what these endpoints assert. */
  private hasLegalEntityAccess(accessType: LegalEntityAccessType): boolean {
    return this._permissions.some((permission) => {
      if (!(permission.resource instanceof LegalEntityAccess)) return false;
      const resource = permission.resource;
      return (
        resource.accessType === accessType &&
        resource.scope >= Scope["Entity-Wide"] &&
        resource.accessLevel >= AccessLevel["Read-Only"]
      );
    });
  }
}
